import { join } from 'node:path';
import { existsSync } from 'node:fs';
import { env } from 'node:process';
import { getDataDirPath } from '@wix/cli-data-dir';
import { pathExists, readJson, readJsonSync, writeJson } from '@wix/cli-fs';
import {
  userConfigSchema,
  USER_CONFIG_FILENAME,
} from '@wix/cli-core-definitions';
import type { UserConfig } from '@wix/cli-core-definitions';

function getUserConfigPath() {
  return join(getDataDirPath(), USER_CONFIG_FILENAME);
}

export async function readUserConfig() {
  const configPath = getUserConfigPath();
  const isConfigExist = await pathExists(configPath);
  const json = isConfigExist ? await readJson(configPath) : {};
  return userConfigSchema.parse(json);
}

/**
 * Synchronous flavor of `readUserConfig`. Use it only where the answer is
 * needed before the first `await` (e.g. deciding whether the error reporter
 * may send anything at all).
 */
export function readUserConfigSync() {
  const configPath = getUserConfigPath();
  const json = existsSync(configPath) ? readJsonSync(configPath) : {};
  return userConfigSchema.parse(json);
}

export async function updateUserConfig(config: Partial<UserConfig>) {
  const currentConfig = await readUserConfig();
  const newConfig = {
    ...currentConfig,
    ...config,
  };

  const configPath = getUserConfigPath();
  await writeJson(configPath, newConfig);
}

/**
 * `DO_NOT_TRACK=1` (https://donottrack.sh) lets CI pipelines and sandboxed AI
 * agents opt out of telemetry without first running `wix telemetry off`.
 */
export function isTrackingDisabled() {
  return env.DO_NOT_TRACK === '1';
}

/**
 * Whether telemetry of any kind (BI, Panorama and Sentry error reports) may
 * leave the machine. `DO_NOT_TRACK` wins over the persisted user preference.
 */
export async function isTelemetryEnabled() {
  if (isTrackingDisabled()) {
    return false;
  }
  const { telemetry } = await readUserConfig();
  return telemetry;
}

export function isTelemetryEnabledSync() {
  if (isTrackingDisabled()) {
    return false;
  }
  return readUserConfigSync().telemetry;
}
